import React, { useState, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  IconButton,
  InputBase,
  Box,
  Avatar,
  Typography,
  Paper,
  Popper,
  ClickAwayListener,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Fade,
  Divider,
  Menu,
  MenuItem,
  Badge,
  Button,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import NotificationsIcon from "@mui/icons-material/Notifications";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import CircleIcon from "@mui/icons-material/Circle";
import DescriptionIcon from "@mui/icons-material/Description";
import TaskIcon from "@mui/icons-material/Task";
import LogoutIcon from "@mui/icons-material/Logout";
import PersonIcon from "@mui/icons-material/Person";
import ChatIcon from "@mui/icons-material/Chat";
import AssignmentIcon from "@mui/icons-material/Assignment";
import ReportIcon from "@mui/icons-material/Report";
import AppRegistrationIcon from "@mui/icons-material/AppRegistration";
import FollowTheSignsIcon from "@mui/icons-material/FollowTheSigns";
import styled from "@mui/material/styles/styled";
import { useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { useNotification } from "../context/NotificationContext";
import { searchProject } from "../services/searchApi";
import { api } from "../services/api";
import { API_BASE_URL } from "../config";
import {
  SmartToy,
  RocketLaunch,
  Pets,
  BugReport,
  AutoAwesome,
  EmojiNature,
  Psychology,
  Science,
  Biotech,
  PsychologyAlt,
  Memory,
  Lightbulb,
  School,
  Code,
  Terminal,
  DataObject,
  Storage,
  Cloud,
  Security,
  Shield,
  Lock,
  Key,
  VpnKey,
  Fingerprint,
  VerifiedUser,
  Gavel,
  Balance,
  Scale,
  GavelOutlined,
  BalanceOutlined,
  ScaleOutlined,
  GavelRounded,
  BalanceRounded,
  ScaleRounded,
  GavelSharp,
  BalanceSharp,
  ScaleSharp,
  GavelTwoTone,
  BalanceTwoTone,
  ScaleTwoTone,
} from "@mui/icons-material";

const Search = styled("div")(({ theme }) => ({
  position: "relative",
  borderRadius: theme.shape.borderRadius,
  backgroundColor: "rgba(255, 255, 255, 0.1)",
  "&:hover": {
    backgroundColor: "rgba(255, 255, 255, 0.15)",
  },
  marginLeft: "auto",
  width: "400px",
  minWidth: "auto",
  border: "1px solid rgba(255, 255, 255, 0.2)",
}));

const SearchIconWrapper = styled("div")(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: "100%",
  position: "absolute",
  pointerEvents: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "rgba(255, 255, 255, 0.7)",
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: "white",
  width: "100%",
  "& .MuiInputBase-input": {
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    width: "100%",
    "&::placeholder": {
      color: "rgba(255, 255, 255, 0.5)",
      opacity: 1,
    },
  },
}));

const ICON_MAP = {
  SmartToy,
  RocketLaunch,
  Pets,
  BugReport,
  AutoAwesome,
  EmojiNature,
  Psychology,
  Science,
  Biotech,
  PsychologyAlt,
  Memory,
  Lightbulb,
  School,
  Code,
  Terminal,
  DataObject,
  Storage,
  Cloud,
  Security,
  Shield,
  Lock,
  Key,
  VpnKey,
  Fingerprint,
  VerifiedUser,
  Gavel,
  Balance,
  Scale,
  GavelOutlined,
  BalanceOutlined,
  ScaleOutlined,
  GavelRounded,
  BalanceRounded,
  ScaleRounded,
  GavelSharp,
  BalanceSharp,
  ScaleSharp,
  GavelTwoTone,
  BalanceTwoTone,
  ScaleTwoTone,
};

// Default theme configuration
const DEFAULT_THEME = {
  bgColor: "#ffffff",
  textColor: "#1a1a1a",
  searchBg: "rgba(0, 0, 0, 0.05)",
  searchHoverBg: "rgba(0, 0, 0, 0.08)",
  searchBorder: "rgba(0, 0, 0, 0.1)",
};

function Header({ currentProject }) {
  const [searchQuery, setSearchQuery] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const [_isSearching, setIsSearching] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [supplierInfo, setSupplierInfo] = useState(null);
  const [orgLogo, setOrgLogo] = useState(null);
  const [notificationAnchor, setNotificationAnchor] = useState(null);

  const navigate = useNavigate();
  const { handleLogout, user, organization, _updateUser, setOrganization } = useApp();
  const { backendNotifications, unreadCount, markAsRead, markAllAsRead } = useNotification();

  const handleNotificationClick = (event) => {
    setNotificationAnchor(event.currentTarget);
  };

  const handleNotificationClose = () => {
    setNotificationAnchor(null);
  };

  const handleNotificationItemClick = (notification) => {
    if (!notification.read) {
      markAsRead(notification.id);
    }

    if (notification.resource_link) {
      navigate(notification.resource_link);
      handleNotificationClose();
    }
  };

  const handleMarkAllRead = () => {
    markAllAsRead();
    // Close notification menu immediately after marking all as read
    setTimeout(() => {
      handleNotificationClose();
    }, 100);
  };

  // Use default theme
  const theme = DEFAULT_THEME;

  // Fetch supplier information for supplier users
  useEffect(() => {
    const fetchSupplierInfo = async () => {
      if (user?.role === "cbSupplier") {
        try {
          const profile = await api.getMyProfile();
          setSupplierInfo(profile);
        } catch (error) {
          console.error("Error fetching supplier profile:", error);
        }
      }
    };

    fetchSupplierInfo();
  }, [user?.role]);

  // Fetch organization logo from database
  useEffect(() => {
    const fetchOrgLogo = async () => {
      if (organization?.org_id) {
        try {
          // Check if organization has a database-stored logo
          if (organization.logo && organization.logo.startsWith("db://")) {
            // Use the same API base URL as the api service
            const baseUrl = API_BASE_URL.endsWith("/") ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
            const logoUrl = `${baseUrl}/api/orgs/${organization.org_id}/logo`;
            setOrgLogo(logoUrl);
          } else {
            setOrgLogo(null);
          }
        } catch (error) {
          console.error("Error fetching organization logo:", error);
          setOrgLogo(null);
        }
      } else {
        setOrgLogo(null);
      }
    };

    fetchOrgLogo();
  }, [organization]);

  // Force refresh organization data when component mounts
  useEffect(() => {
    const refreshOrgData = async () => {
      if (user?.org_id) {
        try {
          const orgData = await api.getOrg(user.org_id);
          // Update the organization in context
          setOrganization(orgData);
        } catch (error) {
          console.error("Error refreshing organization data:", error);
        }
      }
    };

    refreshOrgData();
  }, [user?.org_id]);

  // Get icon for search result type
  const getResultIcon = (type) => {
    switch (type) {
      case "conversation":
        return <ChatIcon />;
      case "builtin_task":
      case "task":
        return <AssignmentIcon />;
      case "jira_task":
        return <TaskIcon />;
      case "report":
        return <ReportIcon />;
      case "application":
        return <AppRegistrationIcon />;
      case "followup":
        return <FollowTheSignsIcon />;
      default:
        return <DescriptionIcon />;
    }
  };

  // Handle search input change with debounce
  const handleSearchChange = (event) => {
    const newQuery = event.target.value;
    setSearchQuery(newQuery);

    // Clear previous timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    // Show loading indicator immediately
    setIsSearching(true);
    setAnchorEl(event.currentTarget.parentElement); // Anchor to the Search component

    // If query is empty, clear results
    if (newQuery.trim() === "") {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }

    // Set a new timeout to perform search after delay
    setSearchTimeout(
      setTimeout(async () => {
        if (currentProject) {
          try {
            const data = await searchProject(currentProject.id, newQuery.trim());

            // Robust mapping with validation and filtering
            const formattedResults = data
              .map((item) => {
                if (!item || typeof item !== "object") {
                  console.error("Invalid item received from search API:", item);
                  return null;
                }

                const id = item.id ?? `invalid-${Math.random()}`;
                const type = item.type ?? "unknown";
                const title = item.title ?? "Untitled Result";
                const content = item.content ?? "";

                return {
                  id: id,
                  type: type,
                  title: title,
                  subtitle: content.substring(0, 100) + (content.length > 100 ? "..." : ""),
                  icon: getResultIcon(type),
                  originalResult: item, // Keep original for navigation
                };
              })
              .filter(Boolean); // Remove any null results

            setSearchResults(formattedResults);
          } catch (error) {
            console.error("Search error:", error);
            setSearchResults([]); // Clear results on error
          }
        }
        setIsSearching(false);
      }, 500)
    ); // 500ms debounce
  };

  // Handle search result click
  const handleResultClick = (result) => {
    // Add defensive check for result object
    if (!result || typeof result !== "object" || !result.originalResult) {
      console.error("Invalid result object passed to handleResultClick:", result);
      setAnchorEl(null); // Close popper
      setSearchQuery(""); // Clear search
      return; // Prevent further execution
    }

    setAnchorEl(null);
    setSearchQuery("");

    // Use originalResult for navigation
    const originalResult = result.originalResult;

    // Navigate based on result type
    switch (originalResult.type) {
      case "conversation":
        navigate(`/project/${currentProject.id}/CertGPT`);
        break;
      case "task":
      case "builtin_task":
        navigate(`/project/${currentProject.id}/tasks/${originalResult.id}`);
        break;
      case "jira_task":
        navigate(`/project/${currentProject.id}/jira-tasks`);
        break;
      case "report":
        if (originalResult.id) {
          navigate(`/project/${currentProject.id}/reports/${originalResult.id}`);
        } else {
          navigate(`/project/${currentProject.id}/reports`);
        }
        break;
      case "application":
        // Check if we have a specific application ID to navigate to
        if (originalResult.metadata && originalResult.metadata.database_id) {
          // Navigate to the specific application edit page
          navigate(
            `/project/${currentProject.id}/autofill-form?application_id=${originalResult.metadata.database_id}`
          );
        } else {
          // Navigate to the general applications page
          navigate(`/project/${currentProject.id}/applications`);
        }
        break;
      case "followup":
        navigate(`/project/${currentProject.id}/followups`);
        break;
      case "unknown": // Handle the default case from mapping
        console.warn("Clicked on result with unknown type:", originalResult);
        break;
      default:
        console.warn("Unknown result type:", originalResult.type);
    }
  };

  const handleClickAway = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const handleUserMenuClick = (event) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleProfileClick = () => {
    handleUserMenuClose();
    navigate("/profile");
  };

  const handleLogoutClick = () => {
    handleLogout();
    handleUserMenuClose();
    navigate("/login");
  };

  const getCurrentAvatar = () => {
    try {
      const parsedAvatar = user?.avatar ? JSON.parse(user.avatar) : null;
      if (parsedAvatar && parsedAvatar.iconName) {
        return parsedAvatar;
      }
      return { name: "Default", iconName: "SmartToy", color: "#1976d2" };
    } catch {
      return { name: "Default", iconName: "SmartToy", color: "#1976d2" };
    }
  };

  const getIconComponent = (iconName) => {
    return ICON_MAP[iconName] || SmartToy;
  };

  return (
    <AppBar
      position="static"
      sx={{
        backgroundColor: theme.bgColor,
        boxShadow: "none",
        borderBottom: `1px solid ${theme.searchBorder}`,
      }}
    >
      <Toolbar>
        {/* Logo */}
        {orgLogo ? (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Box
              component="img"
              src={orgLogo}
              alt="Organization Logo"
              sx={{
                height: 48,
                width: "auto",
                mr: 3,
              }}
            />
          </Box>
        ) : (
          <Box sx={{ display: "flex", alignItems: "center", mr: 3 }}>
            <Typography
              variant="h6"
              sx={{
                color: theme.textColor,
                fontWeight: "bold",
                fontSize: "18px",
              }}
            >
              {organization?.org_name || "Organization"}
            </Typography>
          </Box>
        )}

        {/* Welcome Banner for Suppliers */}
        {user?.role === "cbSupplier" && (
          <Box
            sx={{
              display: "flex",
              alignItems: "left",
              flex: 1,
              justifyContent: "left",
              px: 2,
            }}
          >
            <Typography
              variant="h3"
              sx={{
                color: "primary.main",
                fontWeight: 800,
                textAlign: "center",
                fontSize: "1.5rem",
              }}
            >
              {supplierInfo?.company_name
                ? `Supplier Portal for ${supplierInfo.company_name}`
                : "Supplier Portal"}
            </Typography>
          </Box>
        )}

        {/* Search Box - Hide for suppliers and cbExec users */}
        {user?.role !== "cbSupplier" && user?.role !== "cbExec" && (
          <Box sx={{ display: "flex", flex: 1, justifyContent: "flex-end" }}>
            <Search
              sx={{
                backgroundColor: theme.searchBg,
                "&:hover": {
                  backgroundColor: theme.searchHoverBg,
                },
                border: `1px solid ${theme.searchBorder}`,
              }}
            >
              <SearchIconWrapper sx={{ color: theme.textColor }}>
                <SearchIcon />
              </SearchIconWrapper>
              <StyledInputBase
                placeholder={`Search ${currentProject?.name || "all projects"}...`}
                value={searchQuery}
                onChange={handleSearchChange}
                inputProps={{ "aria-label": "search" }}
                sx={{
                  color: theme.textColor,
                  "& .MuiInputBase-input::placeholder": {
                    color: theme.textColor + "80", // 50% opacity
                    opacity: 1,
                  },
                }}
              />
            </Search>
          </Box>
        )}

        {/* Spacer for suppliers and cbExec users to keep buttons on the right */}
        {(user?.role === "cbSupplier" || user?.role === "cbExec") && <Box sx={{ flex: 1 }} />}

        {/* Right Section */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, ml: 2 }}>
          <IconButton sx={{ color: theme.textColor }}>
            <HelpOutlineIcon />
          </IconButton>

          <IconButton sx={{ color: theme.textColor }} onClick={handleNotificationClick}>
            <Badge badgeContent={unreadCount} color="error">
              {unreadCount > 0 ? <NotificationsIcon /> : <NotificationsNoneIcon />}
            </Badge>
          </IconButton>

          <Menu
            anchorEl={notificationAnchor}
            open={Boolean(notificationAnchor)}
            onClose={handleNotificationClose}
            PaperProps={{
              sx: {
                width: 360,
                maxHeight: 500,
                mt: 1.5,
                "& .MuiList-root": {
                  p: 0,
                },
              },
            }}
            transformOrigin={{ horizontal: "right", vertical: "top" }}
            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          >
            <Box
              sx={{
                p: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                borderBottom: "1px solid rgba(0,0,0,0.08)",
              }}
            >
              <Typography variant="subtitle1" fontWeight="bold">
                Notifications
              </Typography>
              {unreadCount > 0 && (
                <Button
                  size="small"
                  startIcon={<DoneAllIcon />}
                  onClick={handleMarkAllRead}
                  sx={{ textTransform: "none", fontSize: "0.8rem" }}
                >
                  Mark all read
                </Button>
              )}
            </Box>

            <List sx={{ overflow: "auto", maxHeight: 400 }}>
              {backendNotifications.filter((n) => !n.read).length === 0 ? (
                <Box sx={{ p: 3, textAlign: "center" }}>
                  <Typography variant="body2" color="text.secondary">
                    No unread notifications
                  </Typography>
                </Box>
              ) : (
                backendNotifications
                  .filter((n) => !n.read)
                  .map((notification) => (
                    <React.Fragment key={notification.id}>
                      <ListItem
                        button
                        onClick={() => handleNotificationItemClick(notification)}
                        alignItems="flex-start"
                        sx={{
                          bgcolor: notification.read ? "transparent" : "rgba(25, 118, 210, 0.04)",
                          "&:hover": {
                            bgcolor: notification.read
                              ? "rgba(0,0,0,0.04)"
                              : "rgba(25, 118, 210, 0.08)",
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 40, mt: 0.5 }}>
                          {!notification.read && (
                            <CircleIcon sx={{ fontSize: 10, color: "#1976d2" }} />
                          )}
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "flex-start",
                              }}
                            >
                              <Typography
                                variant="body2"
                                fontWeight={notification.read ? 400 : 600}
                                sx={{ mr: 1 }}
                              >
                                {notification.title}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ whiteSpace: "nowrap", fontSize: "0.7rem" }}
                              >
                                {/* Using simple date format to avoid date-fns dependency issues if not installed */}
                                {new Date(notification.created_at).toLocaleDateString()}
                              </Typography>
                            </Box>
                          }
                          secondary={
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                                fontSize: "0.85rem",
                                mt: 0.5,
                              }}
                            >
                              {notification.message}
                            </Typography>
                          }
                        />
                      </ListItem>
                      <Divider component="li" />
                    </React.Fragment>
                  ))
              )}
            </List>
          </Menu>

          <IconButton color="inherit" onClick={handleUserMenuClick}>
            <Avatar
              sx={{
                width: 32,
                height: 32,
                bgcolor: getCurrentAvatar().color,
              }}
            >
              {(() => {
                const Icon = getIconComponent(getCurrentAvatar().iconName);
                return <Icon sx={{ fontSize: "1.2rem", color: "white" }} />;
              })()}
            </Avatar>
          </IconButton>
        </Box>

        {/* User Menu */}
        <Menu
          anchorEl={userMenuAnchor}
          open={Boolean(userMenuAnchor)}
          onClose={handleUserMenuClose}
          PaperProps={{
            sx: {
              minWidth: 300,
            },
          }}
        >
          <Box sx={{ p: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
              {user?.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {user?.email}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {user?.role}
            </Typography>
            {organization && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  mt: 1,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  fontWeight: 500,
                }}
              >
                {organization.org_name}
              </Typography>
            )}
          </Box>
          <Divider />

          <MenuItem onClick={handleProfileClick}>
            <ListItemIcon>
              <PersonIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Profile</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleLogoutClick}>
            <ListItemIcon>
              <LogoutIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Logout</ListItemText>
          </MenuItem>
        </Menu>

        <ClickAwayListener onClickAway={handleClickAway}>
          <Popper
            open={open && searchResults.length > 0}
            anchorEl={anchorEl}
            placement="bottom-start"
            transition
            sx={{
              width: anchorEl?.offsetWidth,
              zIndex: 1300,
              mt: 1,
            }}
          >
            {({ TransitionProps }) => (
              <Fade {...TransitionProps} timeout={350}>
                <Paper
                  elevation={3}
                  sx={{
                    maxHeight: "400px",
                    overflow: "auto",
                  }}
                >
                  <List sx={{ py: 0 }}>
                    {searchResults.map((result, index) => (
                      <React.Fragment key={index}>
                        {index > 0 && <Divider />}
                        <ListItem button onClick={() => handleResultClick(result)}>
                          <ListItemIcon sx={{ minWidth: 40 }}>{result.icon}</ListItemIcon>
                          <ListItemText
                            primary={result.title}
                            secondary={
                              <Typography variant="caption" color="text.secondary">
                                {result.type
                                  .replace("_", " ")
                                  .replace(/\b\w/g, (l) => l.toUpperCase())}{" "}
                                - {result.subtitle}
                              </Typography>
                            }
                            primaryTypographyProps={{
                              variant: "body2",
                              fontWeight: 500,
                            }}
                          />
                        </ListItem>
                      </React.Fragment>
                    ))}
                  </List>
                </Paper>
              </Fade>
            )}
          </Popper>
        </ClickAwayListener>
      </Toolbar>
    </AppBar>
  );
}

export default Header;
